import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import {
  isV2OrganisationsAndRolesCopyContext,
  productCatalogManufacturerCopyKey,
} from '@/features/associated-parties/associatedPartyCopy'
import { getAssociatedPartiesListPath } from '@/features/associated-parties/associatedPartyPaths'
import {
  useListAssociatedParties,
} from '@/shared/hooks/api/associatedParties'
import { FormControl } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatPartyCityCountry } from '@/features/associated-parties/formatAddress'
import { useCountryDisplayName } from '@/features/associated-parties/useCountryDisplayName'

export const NO_MANUFACTURER_VALUE = '__no_manufacturer__'

type ProductManufacturerSelectProps = {
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
  enabled?: boolean
  includeNoManufacturerOption?: boolean
  inForm?: boolean
  triggerId?: string
}

export function ProductManufacturerSelect({
  value,
  onValueChange,
  disabled = false,
  enabled = true,
  includeNoManufacturerOption = false,
  inForm = true,
  triggerId,
}: ProductManufacturerSelectProps) {
  const { t } = useTranslation('productCatalog')
  const { pathname } = useLocation()
  const associatedPartiesListPath = getAssociatedPartiesListPath(pathname)
  const useV2Copy = isV2OrganisationsAndRolesCopyContext(pathname)
  const manufacturerCopy = (key: string, v1Key: string) =>
    t(useV2Copy ? productCatalogManufacturerCopyKey(pathname, key) : v1Key)
  const getCountryName = useCountryDisplayName()
  const { data: associatedParties = [], isLoading } = useListAssociatedParties({
    enabled,
  })
  const showNoManufacturerOption = includeNoManufacturerOption
  const showEmptyState = associatedParties.length === 0
  const showAssociatedParties = associatedParties.length > 0
  const selectItems = useMemo(() => {
    const items: Array<{ value: string; label: string }> = []
    if (showNoManufacturerOption) {
      items.push({
        value: NO_MANUFACTURER_VALUE,
        label: t('detail.manufacturer.dialog.removeOption'),
      })
    }
    for (const party of associatedParties) {
      const location = formatPartyCityCountry(party, getCountryName)
      const label = [party.name, location].filter(Boolean).join(' ')
      items.push({ value: party.id, label })
    }
    return items
  }, [associatedParties, getCountryName, showNoManufacturerOption, t])

  const selectTrigger = (
    <SelectTrigger id={triggerId} className='w-full'>
      <SelectValue
        placeholder={manufacturerCopy(
          'placeholder',
          'dialog.fields.associatedPartyPlaceholder'
        )}
      />
    </SelectTrigger>
  )

  return (
    <>
      <Select
        value={value}
        onValueChange={(value) => onValueChange(value ?? '')}
        disabled={disabled || isLoading}
        items={selectItems}
      >
        {inForm ? <FormControl>{selectTrigger}</FormControl> : selectTrigger}
        <SelectContent>
          {showNoManufacturerOption && (
            <SelectItem value={NO_MANUFACTURER_VALUE}>
              {t('detail.manufacturer.dialog.removeOption')}
            </SelectItem>
          )}
          {showEmptyState && (
            <div className='px-2 py-1.5 text-sm text-muted-foreground'>
              {manufacturerCopy('empty', 'detail.manufacturer.dialog.empty')}
            </div>
          )}
          {showAssociatedParties &&
            associatedParties.map((party) => {
              const location = formatPartyCityCountry(party, getCountryName)
              const label = [party.name, location].filter(Boolean).join(' ')
              return (
                <SelectItem key={party.id} value={party.id}>
                  {label}
                </SelectItem>
              )
            })}
        </SelectContent>
      </Select>
      <p className='text-sm text-muted-foreground italic'>
        {manufacturerCopy('createHint', 'detail.manufacturer.dialog.createHint')}{' '}
        <Link
          to={associatedPartiesListPath}
          className='font-medium text-primary underline-offset-4 hover:underline'
        >
          {manufacturerCopy(
            'addOrganisation',
            'detail.manufacturer.dialog.addAssociatedParty'
          )}
        </Link>
      </p>
    </>
  )
}
